// Shared i18n. English is the source catalogue; Arabic must define every key (enforced by type).
// PD-011: Arabic is a development draft, unreviewed until decision H-10 names a reviewer.
import { ar } from './ar.ts';
import { en } from './en.ts';
import type { Catalogue, Message, MessageKey } from './types.ts';

export type { Catalogue, Message, MessageKey, PluralMessage } from './types.ts';
export * from './format.ts';

export const LOCALES = ['en', 'ar'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';

/** Whether each catalogue has had an approved human review (H-10). */
export const LOCALE_REVIEWED: Readonly<Record<Locale, boolean>> = { en: true, ar: false };

export const catalogues: Readonly<Record<Locale, Catalogue>> = { en, ar };

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

export function direction(locale: Locale): 'ltr' | 'rtl' {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

/** PD-012: Latin digits and Gregorian calendar in Arabic. */
export function intlLocale(locale: Locale): string {
  return locale === 'ar' ? 'ar-AE-u-nu-latn' : 'en-GB';
}

/** Pick a supported locale from an Accept-Language header (q-values honoured). */
export function negotiate(header: string | null | undefined): Locale {
  if (!header) return DEFAULT_LOCALE;
  const ranked = header
    .split(',')
    .map((part) => {
      const [tag, ...params] = part.trim().split(';');
      const q = params.map((p) => p.trim()).find((p) => p.startsWith('q='));
      return { tag: (tag ?? '').toLowerCase(), q: q ? Number(q.slice(2)) || 0 : 1 };
    })
    .filter((x) => x.tag)
    .sort((a, b) => b.q - a.q);
  for (const { tag } of ranked) {
    const base = tag.split('-')[0];
    if (isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}

export type Params = Readonly<Record<string, string | number>>;
export type MsgRef = MessageKey | { readonly key: MessageKey; readonly params?: Params };

export function isMessageKey(value: unknown): value is MessageKey {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(en, value);
}

const pluralCache = new Map<Locale, Intl.PluralRules>();

function select(locale: Locale, message: Message, params: Params | undefined): string {
  if (typeof message === 'string') return message;
  const count = Number(params?.count ?? 0);
  let rules = pluralCache.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(intlLocale(locale));
    pluralCache.set(locale, rules);
  }
  const category = count === 0 && message.zero !== undefined ? 'zero' : rules.select(count);
  return message[category] ?? message.other;
}

export function translate(locale: Locale, ref: MsgRef, extra?: Params): string {
  const key = typeof ref === 'string' ? ref : ref.key;
  const params = { ...(typeof ref === 'string' ? {} : ref.params), ...extra };
  const message = catalogues[locale][key] ?? catalogues.en[key];
  const text = select(locale, message, params);
  // A parameter value of the form "@message.key" is itself translated (e.g. location kinds).
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    if (!(name in params)) return whole;
    const value = String(params[name]);
    return value.startsWith('@') && isMessageKey(value.slice(1)) ? translate(locale, value.slice(1) as MessageKey) : value;
  });
}
